<template>
  <v-container fluid>
    <v-row>
      <v-col
        v-for="month in 12"
        :key="month"
        cols="12"
        sm="6"
        md="3"
        class="pa-2"
      >
        <v-card>
          <v-card-title class="text-subtitle-2">{{ monthLabels[month - 1] }}</v-card-title>
          <v-card-text>
            <div v-for="day in getDaysForMonth(month - 1)" :key="day.date" class="mb-1">
              <div
                class="text-caption font-weight-medium cursor-pointer"
                :class="{ 'text-primary': isToday(day.date) }"
                @click="emitCreate(day.date)"
              >
                {{ day.day }}
                <v-chip
                  v-for="event in getEventsForDate(day.date)"
                  :key="event.id"
                  color="primary"
                  size="x-small"
                  class="ml-1"
                >
                  {{ event.name }}
                </v-chip>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  startOfMonth,
  endOfMonth,
  addDays,
  format,
  isValid,
  parseISO
} from 'date-fns';

const props = defineProps<{
  baseDate: Date;
  events: Array<{ id: number; date: string; name: string; original?: any }>;
  moduleName: string;
  title: { singular: string };
  onCreate?: (data: Record<string, any>) => void;
  calendarConfig: {
    dateField: string;
    labelField: string;
    colorField?: string;
  };
}>();

const currentYear = computed(() => props.baseDate.getFullYear());

const monthLabels = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

function normalizeDateInput(input: any): string | null {
  if (!input) return null;
  const dateObj = typeof input === 'string' ? parseISO(input) : input;
  if (!isValid(dateObj)) return null;
  return format(dateObj, 'yyyy-MM-dd');
}

function getEventsForDate(dateStr: string) {
  const field = props.calendarConfig?.dateField || 'date';
  return props.events
    .filter(e => normalizeDateInput(e.original?.[field]) === dateStr)
    .sort((a, b) => new Date(a.original?.[field]).getTime() - new Date(b.original?.[field]).getTime());
}

function getDaysForMonth(month: number) {
  const start = startOfMonth(new Date(currentYear.value, month));
  const end = endOfMonth(start);
  const days = [];

  for (let date = start; date <= end; date = addDays(date, 1)) {
    days.push({ date: format(date, 'yyyy-MM-dd'), day: format(date, 'd') });
  }

  return days;
}

function isToday(dateStr: string) {
  return format(new Date(), 'yyyy-MM-dd') === dateStr;
}

function emitCreate(date: string) {
  if (props.onCreate) props.onCreate({ date });
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
